/**
 * 검색바(위치 | 날짜 | 무엇을) 입력값 → 검색 URL (09-23 개편).
 *
 * 검색바는 세 칸을 따로 고르고 [검색]을 한 번 누른다. 그래서 칸마다 "무엇을 골랐나"만
 * 초안(ComposerDraft)에 들고 있다가, 제출 순간 이 모듈이 search-state 의 전이 규칙으로
 * URL 하나를 만든다. 화면·라우터와 무관한 순수 함수라 테스트로 규칙을 고정한다.
 *
 * 위치 칸 규칙
 *   • current — 검색 페이지에서 위치를 건드리지 않았다. 지금 보는 지역(도시·지명·
 *     지도 영역·내 주변)을 그대로 두고 검색어·필터만 바꾼다.
 *   • place   — 자동완성에서 고른 지명(place-resolver id). 종류별로 scope 를 정한다.
 *   • text    — 고르지 않고 친 글자. 지명으로 풀리면 place, 아니면 검색어에 합친다.
 *   • country — 국내 배송 탭의 나라(전국 택배).
 *   • nearby  — 내 주변. 좌표는 호출자가 구해 넘긴다.
 */

import { CITIES, getCityBounds, getCityById, type City } from "@/config/locations";
import { boundsAroundPoint } from "@/lib/search/place-bounds";
import { parseSearchKeyword, type SupportedLocale } from "@/lib/search/keyword-parser";
import {
  KEYWORD_SLUG,
  citySlug,
  cityBySlug,
  countryByCode,
  countrySlug,
} from "@/lib/search/place-slug";
import { placeById, resolvePlace, type ResolvedPlace } from "@/lib/search/place-resolver";
import { routeCategoryWord } from "@/lib/search/category-matcher";
import {
  serializeSearchUrl,
  transition,
  type SearchBounds,
  type SearchIdentity,
  type SearchUrlState,
} from "@/lib/search/search-state";

export type ComposerUsage = "pickup" | "shipping" | "delivery";

export type ComposerLocation =
  | { kind: "none" }
  | { kind: "current" }
  | {
      kind: "place";
      id: string;
      /**
       * 전 세계 명소("wd:<QID>", 서버 자동완성)는 브라우저 지명 사전에 없다 — 고를 때 받은
       * 좌표·반경을 같이 들고 와서 URL 을 만든다. 사전에 있는 지명은 비워 둔다.
       */
      point?: { lat: number; lng: number; radiusKm: number; name: string };
    }
  | { kind: "text"; text: string }
  | { kind: "country"; code: string }
  | { kind: "nearby" };

export interface ComposerDraft {
  /** null = 이용 방식 필터 없음(탭은 현장 수령으로 보인다) */
  usage: ComposerUsage | null;
  location: ComposerLocation;
  checkin: string | null;
  checkout: string | null;
  categories: string[];
  keyword: string;
}

export const EMPTY_DRAFT: ComposerDraft = {
  usage: null,
  location: { kind: "none" },
  checkin: null,
  checkout: null,
  categories: [],
  keyword: "",
};

/** 빈 검색 상태 — 검색 페이지 밖에서 제출할 때의 출발점. */
export function blankSearchState(): SearchUrlState {
  return {
    identity: { slug: KEYWORD_SLUG, scope: "keyword" },
    filters: { categories: [] },
    view: {},
    passthrough: {},
  };
}

/**
 * 지금 URL → 초안. 검색 페이지면 보고 있는 지역·검색어·날짜·카테고리를 그대로 채워
 * 검색바를 열었을 때 "지금 조건"에서 고쳐 나가게 한다.
 */
export function draftFromUrl(state: SearchUrlState | null): ComposerDraft {
  if (!state) return { ...EMPTY_DRAFT, categories: [] };
  const { identity, filters } = state;
  const hasArea = identity.scope !== "keyword" || !!identity.bounds;
  return {
    usage: filters.usageMethod ?? null,
    location: hasArea ? { kind: "current" } : { kind: "none" },
    checkin: filters.checkin ?? null,
    checkout: filters.checkout ?? null,
    categories: [...filters.categories],
    keyword: identity.q ?? "",
  };
}

const R = 6371;
const rad = (d: number) => (d * Math.PI) / 180;
function km(a: { lat: number; lng: number }, b: { lat: number; lng: number }) {
  const h =
    Math.sin(rad(b.lat - a.lat) / 2) ** 2 +
    Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(rad(b.lng - a.lng) / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

/**
 * 소속 도시가 없는 지명(OSM 명소)의 상위 도시 — 좌표가 도시 반경의 2배 안에 드는 가장
 * 가까운 도시. 못 찾으면 undefined(키워드 스코프 + 반경 박스로 보낸다).
 */
export function nearestCity(center: { lat: number; lng: number }): City | undefined {
  let best: { city: City; d: number } | undefined;
  for (const city of CITIES) {
    if (!city.coordinates) continue;
    const d = km(center, { lat: city.coordinates.latitude, lng: city.coordinates.longitude });
    if (d > (city.radius_km ?? 12) * 2) continue;
    if (!best || d < best.d) best = { city, d };
  }
  return best?.city;
}

/** 지명 하나 → 검색 identity(검색어 제외). */
export function placeIdentity(place: ResolvedPlace): SearchIdentity {
  if (place.kind === "country" && place.country) {
    return { slug: countrySlug(place.country), scope: "country", country: place.country.code };
  }
  if (place.kind === "city") {
    const city = getCityById(place.id);
    if (city) {
      return {
        slug: citySlug(city),
        scope: "city",
        country: city.countryCode,
        bounds: getCityBounds(city.name.en, "en"),
      };
    }
  }
  // 도도부현 — 지역(admin1) 검색. 현청 둘레 박스가 아니라 그 현의 지점 전체(10-19).
  if (place.region) {
    return { slug: place.region.slug, scope: "region", country: place.region.country, region: place.region.code };
  }
  const bounds =
    place.bounds ??
    (place.center ? boundsAroundPoint(place.center.lat, place.center.lng, place.radiusKm ?? 3.3) : null);
  // 상위 도시가 없는 넓은 지명(대만의 현 등, config/search-places/areas) — 전 세계 명소와 같은 모양.
  // 가까운 도시로 접으면 수백 km 밖 도시 이름이 붙고, 이름을 q 로 실으면 0건이 된다.
  if (place.id.startsWith("area:")) {
    return { slug: KEYWORD_SLUG, scope: "keyword", bounds, place: place.id };
  }
  const parent =
    (place.cityId ? getCityById(place.cityId) : undefined) ??
    (place.center ? nearestCity(place.center) : undefined);
  if (parent) {
    return {
      slug: citySlug(parent),
      scope: "district",
      country: parent.countryCode,
      bounds,
      place: place.id,
    };
  }
  // 전 세계 명소 — 좌표 박스 + place id(서버가 헤딩을 되살린다). 이름을 q 로 실으면
  // 상품 검색어가 돼 0건이 된다.
  if (place.id.startsWith("wd:")) {
    return { slug: KEYWORD_SLUG, scope: "keyword", bounds, place: place.id };
  }
  // 상위 도시를 모르는 지명 — 서버가 원문으로 다시 풀 수 있게 이름을 q 로 싣는다.
  return { slug: KEYWORD_SLUG, scope: "keyword", bounds, q: place.label };
}

/** 검색어만 있을 때(위치 칸 비움) — "서울 갤럭시"처럼 지명이 섞였으면 떼어 낸다. */
function keywordIdentity(
  keyword: string,
  locale: SupportedLocale,
  preferCityId?: string,
): SearchIdentity {
  const parsed = parseSearchKeyword(keyword, locale, { preferCityId });
  const rest = parsed.productKeywords.join(" ") || undefined;
  if (parsed.location) {
    const place = placeById(parsed.location.id);
    if (place) {
      const id = placeIdentity(place);
      const q = [id.q, rest].filter(Boolean).join(" ") || undefined;
      return { ...id, q };
    }
  }
  return { slug: KEYWORD_SLUG, scope: "keyword", q: keyword.trim() || undefined };
}

export interface ComposeOptions {
  locale: string;
  /** 동명 지명 힌트(지금 보는 도시) */
  preferCityId?: string;
  /** location.kind === "nearby" 일 때 필수 */
  nearbyBounds?: SearchBounds;
  now?: number;
}

export interface ComposedSearch {
  url: string;
  /** 최근 검색 칩용 — identity 만(필터·뷰·locale 접두사 제외) */
  href: string;
}

/**
 * 초안 → 검색 URL.
 *
 * @param current 지금 검색 상태(검색 페이지가 아니면 blankSearchState()). 가격·정렬처럼
 *   검색바에 없는 필터는 여기서 나른다.
 */
export function composeSearch(
  current: SearchUrlState,
  draft: ComposerDraft,
  opts: ComposeOptions,
): ComposedSearch {
  const locale = opts.locale as SupportedLocale;
  const keyword = draft.keyword.trim();
  const now = opts.now ?? Date.now();

  let next: SearchUrlState;
  const loc = draft.location;
  if (loc.kind === "nearby" && opts.nearbyBounds) {
    next = transition(current, { type: "nearby", bounds: opts.nearbyBounds }, now);
    next.identity.q = keyword || undefined;
  } else if (loc.kind === "current") {
    next = transition(current, {
      type: "newSearch",
      identity: { ...current.identity, q: keyword || undefined },
    }, now);
    // 지도 영역·내 주변 결과에서 검색어만 바꿨다 — 새 박스가 아니니 카메라를 맞추지 않는다.
    if (current.identity.locationSearch) next.view = {};
  } else {
    let identity: SearchIdentity;
    if (loc.kind === "place") {
      const place =
        placeById(loc.id) ??
        (loc.point
          ? {
              id: loc.id,
              kind: "spot" as const,
              label: loc.point.name,
              names: [],
              center: { lat: loc.point.lat, lng: loc.point.lng },
              radiusKm: loc.point.radiusKm,
            }
          : null);
      identity = place
        ? withKeyword(placeIdentity(place), keyword)
        : keywordIdentity(keyword, locale, opts.preferCityId);
    } else if (loc.kind === "country") {
      const country = countryByCode(loc.code);
      identity = country
        ? { slug: countrySlug(country), scope: "country", country: country.code, q: keyword || undefined }
        : keywordIdentity(keyword, locale, opts.preferCityId);
    } else if (loc.kind === "text" && loc.text.trim()) {
      const place = resolvePlace(loc.text, { preferCityId: opts.preferCityId });
      identity = place
        ? withKeyword(placeIdentity(place), keyword)
        : keywordIdentity(`${loc.text.trim()} ${keyword}`.trim(), locale, opts.preferCityId);
    } else {
      identity = keywordIdentity(keyword, locale, opts.preferCityId);
    }
    next = transition(current, { type: "newSearch", identity }, now);
    if (!identity.bounds) next.view = {};
  }

  // 검색어가 카테고리 낱말("렌터카", "바이크", "レンタカー")이면 검색어 대신 카테고리로 —
  // 지명은 위에서 이미 떼어 냈으므로 "도쿄 바이크"는 도쿄 + 오토바이가 된다(category-matcher).
  // 낱말 뜻은 화면 언어를 따른다 — "bike"는 ko·ja 오토바이, en·zh 자전거.
  const routed = routeCategoryWord(next.identity.q, draft.categories, opts.locale);
  if (routed) next.identity = { ...next.identity, q: routed.q };

  next.filters = {
    ...next.filters,
    categories: routed ? routed.categories : [...draft.categories],
    usageMethod: draft.usage ?? undefined,
    checkin: draft.checkin ?? undefined,
    checkout: draft.checkout ?? undefined,
  };

  const url = serializeSearchUrl(opts.locale, next);
  // 최근 검색 칩은 identity 만 싣는다 — 낱말이 카테고리가 됐으면 그 카테고리는 남긴다
  // (안 남기면 "렌터카" 칩이 전체 상품으로 간다).
  const href = serializeSearchUrl(opts.locale, {
    identity: next.identity,
    filters: { categories: routed ? [routed.added] : [] },
    view: {},
    passthrough: {},
  }).replace(new RegExp(`^/${opts.locale}`), "");
  return { url, href };
}

function withKeyword(identity: SearchIdentity, keyword: string): SearchIdentity {
  const q = [identity.q, keyword].filter(Boolean).join(" ") || undefined;
  return { ...identity, q };
}

/** 지금 URL 의 도시 id — 동명 지명 힌트. */
export function currentCityId(state: SearchUrlState | null): string | undefined {
  if (!state || state.identity.scope === "keyword") return undefined;
  return cityBySlug(state.identity.slug, state.identity.country)?.id;
}
